import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { MdChevronLeft, MdChevronRight, MdFlag, MdTouchApp } from "react-icons/md";
import { ymdDiff } from "./rdSummary";

/**
 * 里程碑時程軸（橫向甘特圖）。
 *
 * 讀法寫在圖的正上方（圖例＋操作說明），不是放在最底下讓人捲到底才看到：
 *   · 每個里程碑一個區塊：區塊標題列寫「里程碑日期 · 里程碑名稱」，底下是排進它的工項。
 *   · 長條＝工項的開始日到截止日（照日期精確畫，不再對齊到整週），顏色＝狀態。
 *   · 區塊內的旗標虛線＝這個里程碑的里程碑日期；長條超過虛線就是排到里程碑日期之後了。
 *   · 整張圖一條藍色直線＝今天。
 * 操作：點長條或左側的工項名稱開啟工項；點里程碑標題列開啟「工項排程」；滾輪或左右鈕捲動。
 *
 * 位置全部用比例算（ratio × 軸寬），軸寬＝週數 × WEEK_W，所以不需要 JS 量測 DOM。
 */

const LABEL_W = 240;
const WEEK_W = 96;
const ROW_H = 40;

/** 長條顏色：跟 Goals.js 的 LEGEND 同一套語意 */
const BAR_COLOR = {
  attention: { bg: "error.main", fg: "background.default" },
  warn: { bg: "warning.main", fg: "background.default" },
  done: { bg: "success.main", fg: "var(--ink-on-fill)" },
  closed: { bg: "grey.500", fg: "background.default" },
  plan: { bg: "primary.main", fg: "var(--ink-on-fill)" },
};

const md = (ymd) => {
  if (!ymd) return "";
  const [, m, d] = ymd.split("-");
  return `${Number(m)}/${Number(d)}`;
};

const SCROLL_SX = {
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  overscrollBehaviorX: "contain",
  "&::-webkit-scrollbar": { width: 0, height: 0 },
};

export default function GoalTimeline({ view, todayYmd, legend, bufferNote, onOpenTask, onOpenGoal }) {
  const totalDays = Math.max(1, view.cols) * 7;
  const chartW = Math.max(1, view.cols) * WEEK_W;

  /** 日期 → 軸上的 x（px）。start=true 取當天左緣，否則取右緣 */
  const xOf = (ymd, edge = "mid") => {
    const d = ymdDiff(view.rangeStart, ymd);
    if (!Number.isFinite(d)) return null;
    const off = edge === "start" ? 0 : edge === "end" ? 1 : 0.5;
    return (Math.min(Math.max(d + off, 0), totalDays) / totalDays) * chartW;
  };
  const todayX = xOf(todayYmd);

  /* ---------- 捲動：滾輪左右跑、捲軸藏起來、左右鈕當提示 ---------- */
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canLeft: false, canRight: false });
  const sync = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollState({ canLeft: el.scrollLeft > 1, canRight: el.scrollLeft < max - 1 });
  }, []);
  const scrollByPage = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    /* React 的 onWheel 是 passive，preventDefault 無效，所以自己掛 listener */
    const onWheel = (e) => {
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", sync, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", sync);
    };
  }, [sync]);
  /* 一進來就把「今天」捲到畫面中間：前後各看得到一段 */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (todayX !== null) el.scrollLeft = Math.max(0, LABEL_W + todayX - el.clientWidth / 2);
    sync();
  }, [todayX, view.rangeStart, view.cols, sync]);

  /* 依里程碑日期由早到晚排；沒有里程碑日期的放最後 */
  const goals = view.goals
    .filter((g) => g.tasks && g.tasks.length > 0)
    .slice()
    .sort((a, b) => (a.endYmd || "9999").localeCompare(b.endYmd || "9999"));
  const barsOf = (gid) => view.bars.filter((b) => String(b.goalId) === String(gid));

  /** 背景：週格線＋今天線，畫在整張圖（標題列以下）的底層 */
  const grid = (
    <Box aria-hidden="true" sx={{ position: "absolute", top: 0, bottom: 0, left: LABEL_W, width: chartW, pointerEvents: "none" }}>
      {view.weeks.map((w, i) => (
        <Box key={w.ymd} sx={{ position: "absolute", top: 0, bottom: 0, left: i * WEEK_W, borderLeft: 1, borderColor: "divider" }} />
      ))}
      {todayX !== null ? (
        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: todayX, width: 2, ml: "-1px", bgcolor: "primary.main", opacity: 0.85 }} />
      ) : null}
    </Box>
  );

  return (
    <Box>
      {/* ---- 讀法與操作：放在圖的正上方 ---- */}
      <Stack spacing={1.25} sx={{ mt: 2, p: 2, borderRadius: "var(--radius-md)", bgcolor: "action.hover" }}>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          {legend.map((l) => (
            <Typography key={l.text} component="span" variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              <Box component="span" aria-hidden="true" sx={{ width: 22, height: 10, borderRadius: "var(--radius-pill)", bgcolor: l.color }} />
              {l.text}
            </Typography>
          ))}
          <Typography component="span" variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <Box component="span" aria-hidden="true" sx={{ width: 2, height: 16, bgcolor: "primary.main" }} />
            今天
          </Typography>
          <Typography component="span" variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <Box component="span" aria-hidden="true" sx={{ width: 0, height: 16, borderLeft: "2px dashed", borderColor: "text.secondary" }} />
            里程碑日期
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <MdTouchApp aria-hidden="true" />
          長條為工項的開始日至截止日。點選長條或工項名稱可開啟工項；點選里程碑標題可前往工項排程。
        </Typography>
        {bufferNote ? (
          <Typography variant="body2" color="text.secondary">
            {bufferNote}
          </Typography>
        ) : null}
      </Stack>

      {/* ---- 左右捲動 ---- */}
      {scrollState.canLeft || scrollState.canRight ? (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" startIcon={<MdChevronLeft aria-hidden="true" />} disabled={!scrollState.canLeft} onClick={() => scrollByPage(-1)}>
            向前
          </Button>
          <Button size="small" variant="outlined" endIcon={<MdChevronRight aria-hidden="true" />} disabled={!scrollState.canRight} onClick={() => scrollByPage(1)}>
            向後
          </Button>
          <Typography component="span" variant="body2" color="text.secondary">
            也可使用滑鼠滾輪左右捲動。
          </Typography>
        </Stack>
      ) : null}

      <Box ref={scrollRef} sx={{ ...SCROLL_SX, mt: 2 }}>
        <Box sx={{ position: "relative", width: LABEL_W + chartW + 24, minWidth: "100%" }}>
          {/* ---- 週刻度（今天另外標出來） ---- */}
          <Box sx={{ position: "relative", height: 56, borderBottom: 1, borderColor: "divider" }}>
            {view.weeks.map((w, i) => (
              <Typography
                key={w.ymd}
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ position: "absolute", left: LABEL_W + i * WEEK_W + 6, top: 4, whiteSpace: "nowrap" }}
              >
                {w.label}
              </Typography>
            ))}
            {todayX !== null ? (
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  left: LABEL_W + todayX,
                  top: 30,
                  transform: "translateX(-50%)",
                  px: 0.75,
                  borderRadius: "var(--radius-xs)",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: "var(--t-meta)",
                  lineHeight: "20px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                今天 {md(todayYmd)}
              </Box>
            ) : null}
          </Box>

          {/* ---- 每個里程碑一個區塊 ---- */}
          <Box sx={{ position: "relative" }}>
            {grid}
            {goals.map((g) => {
              const gx = g.endYmd ? xOf(g.endYmd, "end") : null;
              const bars = barsOf(g.id);
              const lineColor = g.level === "attention" ? "error.main" : g.level === "warn" ? "warning.main" : "text.secondary";
              return (
                <Box key={g.id} component="section" aria-label={`里程碑：${g.name}`} sx={{ position: "relative" }}>
                  {/* 里程碑標題列：底色整列，可點的標題固定在左側（橫向捲動時不會跑掉），前往工項排程 */}
                  <Box sx={{ mt: 1.5, borderRadius: "var(--radius-sm)", bgcolor: "action.hover", border: 1, borderColor: "divider" }}>
                    <ButtonBase
                      onClick={() => onOpenGoal(g.id)}
                      sx={{
                        position: "sticky",
                        left: 0,
                        display: "inline-flex",
                        gap: 1,
                        maxWidth: "min(100%, 760px)",
                        minHeight: 40,
                        px: 1.5,
                        borderRadius: "var(--radius-sm)",
                        textAlign: "left",
                        "&:hover .gt-goal": { textDecoration: "underline" },
                        "&.Mui-focusVisible": { outline: "var(--focus-w) solid var(--focus-ring)" },
                      }}
                    >
                      <Box component="span" aria-hidden="true" sx={{ display: "flex", color: lineColor }}>
                        <MdFlag />
                      </Box>
                      <Typography component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                        {g.endYmd ? `${md(g.endYmd)} 里程碑` : "未設定里程碑日期"}
                      </Typography>
                      <Typography component="span" className="gt-goal" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {g.name}
                      </Typography>
                      {g.createdBy ? (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                          建立者：{g.createdBy}
                        </Typography>
                      ) : null}
                    </ButtonBase>
                  </Box>

                  <Box sx={{ position: "relative" }}>
                    {/* 這個里程碑的里程碑日期：只畫在自己的區塊裡，才看得出是哪一個里程碑的期限 */}
                    {gx !== null ? (
                      <Box
                        aria-hidden="true"
                        sx={{ position: "absolute", top: 0, bottom: 0, left: LABEL_W + gx, borderLeft: "2px dashed", borderColor: lineColor, zIndex: 1 }}
                      />
                    ) : null}

                    {bars.map((b) => {
                      const c = BAR_COLOR[b.level] || BAR_COLOR.plan;
                      const s = b.startYmd || b.dueYmd;
                      const e = b.dueYmd || b.startYmd;
                      const x1 = s ? xOf(s, "start") : null;
                      const x2 = e ? xOf(e, "end") : null;
                      const range = s && e ? (s === e ? md(s) : `${md(s)} – ${md(e)}`) : "";
                      const tip = `${b.title}（${b.assignee || "未指派"}）${range ? `　${range}` : ""}　${b.text}`;
                      const label = range ? `${range} · ${b.text}` : b.text;
                      /* 長條太短放不下字（一兩天的工項）：長條只留圖示，字放到長條右邊 */
                      const barW = x1 !== null && x2 !== null ? Math.max(x2 - x1, 28) : 0;
                      const outside = barW < 132;
                      return (
                        <Box key={b.id} sx={{ position: "relative", height: ROW_H }}>
                          <ButtonBase
                            onClick={() => b.id != null && onOpenTask(b.id)}
                            title={tip}
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              width: LABEL_W,
                              height: ROW_H,
                              justifyContent: "flex-start",
                              gap: 1,
                              px: 1,
                              bgcolor: "background.paper",
                              borderRadius: "var(--radius-sm)",
                              "&:hover .gt-name": { textDecoration: "underline" },
                            }}
                          >
                            <Typography component="span" className="gt-name" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                              {b.title}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                              {b.assignee || "未指派"}
                            </Typography>
                          </ButtonBase>

                          {x1 !== null && x2 !== null ? (
                            <Tooltip
                              title={
                                <>
                                  <strong>{b.title}</strong>
                                  <br />
                                  {b.assignee || "未指派"}　{range}
                                  <br />
                                  {b.text}
                                </>
                              }
                            >
                              <ButtonBase
                                onClick={() => b.id != null && onOpenTask(b.id)}
                                aria-label={tip}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  height: ROW_H - 12,
                                  left: LABEL_W + x1,
                                  width: barW,
                                  px: outside ? 0 : 1,
                                  gap: 0.5,
                                  justifyContent: outside ? "center" : "flex-start",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  borderRadius: "var(--radius-pill)",
                                  bgcolor: c.bg,
                                  color: c.fg,
                                  fontSize: "var(--t-meta)",
                                  fontWeight: 600,
                                  zIndex: 1,
                                  transition: "filter .15s, box-shadow .15s",
                                  "&:hover": { filter: "brightness(1.08)", boxShadow: 3 },
                                  "&.Mui-focusVisible": { outline: "var(--focus-w) solid var(--focus-ring)", outlineOffset: 2 },
                                }}
                              >
                                <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", flexShrink: 0 }}>
                                  {b.mark}
                                </Box>
                                {outside ? null : (
                                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {label}
                                  </Box>
                                )}
                              </ButtonBase>
                            </Tooltip>
                          ) : null}
                          {x1 !== null && x2 !== null && outside ? (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              aria-hidden="true"
                              sx={{ position: "absolute", left: LABEL_W + x1 + barW + 8, top: 10, whiteSpace: "nowrap", pointerEvents: "none" }}
                            >
                              {label}
                            </Typography>
                          ) : null}
                          {x1 === null || x2 === null ? (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ position: "absolute", left: LABEL_W + 8, top: 10, whiteSpace: "nowrap" }}
                            >
                              尚未排定時間，無法顯示於時間軸
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

GoalTimeline.propTypes = {
  view: PropTypes.object.isRequired,
  todayYmd: PropTypes.string.isRequired,
  legend: PropTypes.array,
  bufferNote: PropTypes.node,
  onOpenTask: PropTypes.func,
  onOpenGoal: PropTypes.func,
};

GoalTimeline.defaultProps = { legend: [], bufferNote: null, onOpenTask: () => {}, onOpenGoal: () => {} };
